import React, { useEffect, useState } from 'react';
import { Sparkle, ArrowRight, Compass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { api } from '@/App';
import { track } from '@/lib/analytics';

const LS_KEY = 'planivance_welcome_seen_v1';

/**
 * First-login welcome modal. Two options:
 *   - Start Guided Tour → triggers the react-joyride tour
 *   - Explore on My Own → dismisses and lets the user roam
 * Auto-opens once per browser+account until user chooses either action.
 * Persistent restart: Sidebar → Help → Restart Guided Tour.
 */
const WelcomeModal = ({ onStartTour }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If the user has already interacted (started or skipped tour), don't re-show
      try {
        const { data } = await api.get('/beta-program/status');
        if (cancelled) return;
        if (data?.tour_completed_at || data?.tour_skipped_at) {
          localStorage.setItem(LS_KEY, '1');
          return;
        }
        if (localStorage.getItem(LS_KEY) === '1') return;
        setOpen(true);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const startTour = async () => {
    localStorage.setItem(LS_KEY, '1');
    setOpen(false);
    try {
      await api.post('/beta-program/tour-status', { event: 'tour_started' });
    } catch (_e) {}
    track('tour_started', { source: 'welcome_modal' });
    onStartTour?.();
  };

  const skip = async () => {
    localStorage.setItem(LS_KEY, '1');
    setOpen(false);
    try {
      await api.post('/beta-program/tour-status', { event: 'tour_skipped' });
    } catch (_e) {}
    track('tour_skipped', { source: 'welcome_modal' });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="welcome-modal"
    >
      <div className="bg-white rounded-md shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="bg-[#0A1128] p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={18} weight="fill" className="text-[#C5A059]" />
            <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-semibold">Private Beta</p>
          </div>
          <h2 className="cabinet-grotesk text-3xl font-medium text-white leading-tight">
            Welcome to PLANIVANCE AI
          </h2>
          <p className="text-sm text-white/70 mt-3 leading-relaxed">
            PLANIVANCE AI helps you organize your financial picture, explore retirement scenarios, and
            better understand the decisions that may affect your financial future.
          </p>
        </div>
        <div className="p-6 space-y-3">
          <Button
            onClick={startTour}
            data-testid="welcome-start-tour-button"
            className="w-full bg-[#0A1128] hover:bg-[#152145] text-white justify-between px-5 py-6 h-auto"
          >
            <span className="flex items-center gap-2">
              <Compass size={18} weight="duotone" className="text-[#C5A059]" />
              <span className="font-semibold">Start Guided Tour</span>
            </span>
            <ArrowRight size={16} />
          </Button>
          <Button
            variant="outline"
            onClick={skip}
            data-testid="welcome-skip-tour-button"
            className="w-full border-slate-300 text-[#4A5568] hover:bg-slate-50 justify-between px-5 py-6 h-auto"
          >
            <span className="font-semibold">Explore on My Own</span>
            <ArrowRight size={16} />
          </Button>
          <p className="text-[11px] text-[#A0AABF] text-center pt-1">
            You can restart the guided tour any time from Support &amp; Resources → Restart Guided Tour.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
